import type {
  NoydbStore,
  DirtyEntry,
  Conflict,
  ConflictStrategy,
  CollectionConflictResolver,
  PushOptions,
  PullOptions,
  PushResult,
  PullResult,
  SyncStatus,
  EncryptedEnvelope,
  SyncMetadata,
  SyncTargetRole,
  ErasureEnforcement,
} from '../../kernel/types.js'
import { NOYDB_SYNC_VERSION } from '../../kernel/types.js'
import { ConflictError } from '../../kernel/errors.js'
import type { NoydbEventEmitter } from '../../kernel/events.js'
import type { SyncPolicy } from '../../kernel/sync-policy.js'
import { SyncScheduler } from '../../kernel/sync-policy.js'
import { isTombstoneShape, isDeleteMarker } from '../../kernel/enclave/index.js'

/** #650 Task 4 (#647) — the declared reserved-lookup (`_dict_*`/`_lookup_*`) collection-name
 *  registry a `SyncEngine` enumerates on pull. Explicit, not a blanket underscore-glob — other
 *  `_`-prefixed namespaces keep their `loadAll`-skip semantics untouched. */
export interface ReservedLookupSource {
  /** Declared reserved-lookup collection names to enumerate on pull. */
  collections(): readonly string[]
}

/** Sync engine: dirty tracking, push, pull, conflict resolution, scheduling. */
export class SyncEngine {
  private readonly local: NoydbStore
  private readonly remote: NoydbStore
  private readonly strategy: ConflictStrategy
  private readonly emitter: NoydbEventEmitter
  private readonly vault: string
  readonly role: SyncTargetRole
  readonly label: string | undefined

  private dirty: DirtyEntry[] = []
  private lastPush: string | null = null
  private lastPull: string | null = null
  private loaded = false
  private autoSyncInterval: ReturnType<typeof setInterval> | null = null
  private isOnline = true

  /** Sync scheduler. Manages push/pull timing. */
  readonly scheduler: SyncScheduler | null

  /** Per-collection conflict resolvers registered by Collection instances. */
  private readonly conflictResolvers = new Map<string, CollectionConflictResolver>()

  /**
   * Expands a collection-name filter to include its satellite pair partner(s)
   * (spec #591 convergence rule 5b) — wired from vault satellite declaration
   * via `setPairExpander`. `undefined` when no satellite has ever been
   * declared for this vault: identity behavior, zero change for non-satellite
   * users.
   */
  private pairExpander?: (names: readonly string[]) => readonly string[]

  /** #598: refreshes Collection in-memory views after a sync-applied local write. Wired by the
   *  vault at open. `action` (#640): `'delete'` for a pulled tombstone/delete-marker, `'put'`
   *  otherwise — classified at `applyRemote`, the one choke point that holds the envelope. */
  private cacheInvalidator?: (collection: string, id: string, action: 'put' | 'delete') => Promise<void>

  /** Wire the Collection-cache invalidation hook (#598). Same injection pattern as `setPairExpander`. */
  setCacheInvalidator(fn: (collection: string, id: string, action: 'put' | 'delete') => Promise<void>): void {
    this.cacheInvalidator = fn
  }

  /** #638 Task 4: opens/flushes the vault's graph-dispatch touch batch around a whole `pull()`/
   *  `push()` call, so N `applyRemote`d records feeding the same derivation/rollup/MV target
   *  recompute once. Wired by the vault at open, mirroring `cacheInvalidator`. */
  private graphBatchController?: { begin(): void; flush(): Promise<void> }

  /** Wire the graph-dispatch batch controller (#638 Task 4). Same injection pattern as `setCacheInvalidator`. */
  setGraphBatchController(controller: { begin(): void; flush(): Promise<void> }): void {
    this.graphBatchController = controller
  }

  /** #650 Task 4 (#647): declared reserved-lookup collections `pull()` enumerates explicitly (the
   *  store's `list()` does not skip `_`-prefixed names, unlike `loadAll()`). Wired by the vault at
   *  open, same injection pattern as `setCacheInvalidator`/`setGraphBatchController`. */
  private reservedLookup?: ReservedLookupSource

  /** Wire the reserved-lookup source (#650 Task 4). */
  setReservedLookupSource(source: ReservedLookupSource): void {
    this.reservedLookup = source
  }

  /** #653: expands a (pair-expanded) collection-name filter to include the reserved `_dict_*`
   *  collections those names depend on via their lookup fields — mirrors `pairExpander` so a
   *  partial `push`/`pull({collections:[...]})` never drops the dictionary a named collection's
   *  labels/membership rely on. Wired unconditionally from the vault's `dictKeyFieldRegistry` via
   *  `setReservedDictExpander` (returns `[]` when the vault has no dict-backed collections); only
   *  `undefined` on a standalone engine that was never vault-attached. */
  private reservedDictExpander?: (names: readonly string[]) => readonly string[]

  /** Wire the reserved-dict expansion function (#653). Same injection pattern as `setPairExpander`. */
  setReservedDictExpander(expander: (names: readonly string[]) => readonly string[]): void {
    this.reservedDictExpander = expander
  }

  constructor(opts: {
    local: NoydbStore
    remote: NoydbStore
    vault: string
    strategy: ConflictStrategy
    emitter: NoydbEventEmitter
    syncPolicy?: SyncPolicy
    role?: SyncTargetRole
    label?: string
  }) {
    this.local = opts.local
    this.remote = opts.remote
    this.vault = opts.vault
    this.strategy = opts.strategy
    this.emitter = opts.emitter
    this.role = opts.role ?? 'sync-peer'
    this.label = opts.label

    // Create scheduler if a policy is provided
    const policy = opts.syncPolicy
    if (policy && policy.push.mode !== 'manual') {
      this.scheduler = new SyncScheduler(policy, {
        push: () => this.push().then(() => {}),
        pull: () => this.pull().then(() => {}),
        getDirtyCount: () => this.dirty.length,
      })
    } else {
      this.scheduler = null
    }
  }

  /** Start the sync scheduler. Called after vault is fully opened. */
  startScheduler(): void {
    this.scheduler?.start()
  }

  /** Stop the sync scheduler. Called on close. */
  stopScheduler(): void {
    this.scheduler?.stop()
  }

  /**
   * Register a per-collection conflict resolver.
   * Called by Collection when `conflictPolicy` is set.
   *
   * Pair-coupled (#591 rule 5b): registering for one satellite-pair member
   * registers the same resolver for both — the pair converges under one
   * resolution policy. Expands with whatever the pair-expander knows *at this
   * call*; resolvers registered before their pair existed are covered by
   * `remirrorPairResolvers` at pair-registration time.
   */
  registerConflictResolver(collection: string, resolver: CollectionConflictResolver): void {
    for (const n of this.pairExpander?.([collection]) ?? [collection]) this.conflictResolvers.set(n, resolver)
  }

  /**
   * Wire a satellite pair-expansion function (vault registration, #591 Task 11).
   * The closure re-reads the live `SatelliteRegistry` on every call, so pairs
   * declared after this is set are still picked up by `push`/`pull` filters.
   */
  setPairExpander(expander: (names: readonly string[]) => readonly string[]): void {
    this.pairExpander = expander
  }

  /**
   * Retroactively mirror per-collection conflict resolvers across a newly
   * registered satellite pair (#591 rule 5b) — covers the NORMAL declaration
   * order, where the base's `conflictPolicy` resolver was registered before
   * the satellite was declared (so the call-time expansion in
   * `registerConflictResolver` saw no pair yet). Tie-break: when BOTH members
   * already carry (different) resolvers, the FIRST name in `names` wins —
   * callers pass `[base, satellite]`, so the base's resolver is canonical.
   * Idempotent; a later explicit registration on either member still
   * overwrites both (last-wins, via the call-time expansion above).
   */
  remirrorPairResolvers(names: readonly string[]): void {
    const canonical = names.map(n => this.conflictResolvers.get(n)).find(r => r !== undefined)
    if (!canonical) return
    for (const n of names) this.conflictResolvers.set(n, canonical)
  }

  /** Record a local change for later push. */
  async trackChange(collection: string, id: string, action: 'put' | 'delete', version: number): Promise<void> {
    await this.ensureLoaded()

    // Deduplicate: if same collection+id already in dirty, update it
    const idx = this.dirty.findIndex(d => d.collection === collection && d.id === id)
    const entry: DirtyEntry = {
      vault: this.vault,
      collection,
      id,
      action,
      version,
      timestamp: new Date().toISOString(),
    }

    if (idx >= 0) {
      this.dirty[idx] = entry
    } else {
      this.dirty.push(entry)
    }

    await this.persistMeta()

    // Notify scheduler of the write (triggers on-change or debounce)
    this.scheduler?.notifyChange()
  }

  /** Remove a dirty entry (satellite fan-out revert cleanup — spec #591). */
  async removeDirty(collection: string, id: string): Promise<void> {
    const before = this.dirty.length
    this.dirty = this.dirty.filter(d => !(d.collection === collection && d.id === id))
    if (this.dirty.length !== before) await this.persistMeta()
  }

  /** Push dirty records to remote adapter. Accepts optional `PushOptions` for partial sync. */
  async push(options?: PushOptions): Promise<PushResult> {
    await this.ensureLoaded()
    this.graphBatchController?.begin() // #638 Task 4

    let pushed = 0
    const conflicts: Conflict[] = []
    const erasures: ErasureEnforcement[] = []
    const errors: Error[] = []
    const completed: number[] = []

    // Partial sync: expand the filter to cover satellite pair partners (#591 rule 5b), then the
    // reserved `_dict_*` collections those (expanded) names depend on (#653) — a locally-dirty
    // dict edit pushes alongside push({collections:['orders']}), symmetric with pull below.
    const expanded = options?.collections ? (this.pairExpander?.(options.collections) ?? options.collections) : null
    const filter = expanded ? new Set([...expanded, ...(this.reservedDictExpander?.(expanded) ?? [])]) : null

    for (let i = 0; i < this.dirty.length; i++) {
      const entry = this.dirty[i]!

      // Partial sync: skip collections not in the filter
      if (filter && !filter.has(entry.collection)) {
        continue
      }

      try {
        if (entry.action === 'delete') {
          await this.remote.delete(this.vault, entry.collection, entry.id)
          completed.push(i)
          pushed++
        } else {
          const envelope = await this.local.get(this.vault, entry.collection, entry.id)
          if (!envelope) {
            // Record was deleted locally after being marked dirty
            completed.push(i)
            continue
          }

          if (isTombstoneShape(envelope)) {
            // #590: a tombstone push is an erasure assertion — unconditional,
            // no CAS, no conflict resolution. Erasure always wins.
            await this.remote.put(this.vault, entry.collection, entry.id, envelope)
            completed.push(i)
            pushed++
            continue
          }

          try {
            await this.remote.put(
              this.vault,
              entry.collection,
              entry.id,
              envelope,
              entry.version - 1,
            )
            completed.push(i)
            pushed++
          } catch (err) {
            if (err instanceof ConflictError) {
              const remoteEnvelope = await this.remote.get(this.vault, entry.collection, entry.id)
              if (remoteEnvelope) {
                if (isTombstoneShape(remoteEnvelope)) {
                  // #590: remote already shredded this record — enforce locally,
                  // never resolve. Resolvers must not overrule an erasure.
                  await this.applyRemote(entry.collection, entry.id, remoteEnvelope)
                  erasures.push(this.reportErasure(entry.collection, entry.id, remoteEnvelope, envelope, 'push'))
                  completed.push(i)
                } else if (
                  remoteEnvelope._v === envelope._v &&
                  isDeleteMarker(remoteEnvelope) !== isDeleteMarker(envelope) &&
                  !this.conflictResolvers.get(entry.collection)
                ) {
                  // #589: a same-_v delete-vs-edit tie on the push channel. handleConflict's db-level
                  // 'version' default would resolve it to local-wins; the tie rule consults ONLY the
                  // per-collection resolver, else delete-wins. (When a per-collection resolver IS set,
                  // fall through to handleConflict, which already honors it — incl. the merged case.)
                  if (isDeleteMarker(remoteEnvelope)) {
                    // remote already deleted → converge locally, drop our (edit) push
                    await this.applyRemote(entry.collection, entry.id, remoteEnvelope)
                    completed.push(i)
                  } else {
                    // our local is the marker → force the delete onto the remote (unconditional put)
                    await this.remote.put(this.vault, entry.collection, entry.id, envelope)
                    completed.push(i)
                    pushed++
                  }
                } else {
                  const { handled, conflict } = await this.handleConflict(
                    entry.collection,
                    entry.id,
                    envelope,
                    remoteEnvelope,
                    'push',
                  )
                  conflicts.push(conflict)
                  if (handled === 'local') {
                    await this.remote.put(this.vault, entry.collection, entry.id, conflict.local)
                    completed.push(i)
                    pushed++
                  } else if (handled === 'remote') {
                    await this.applyRemote(entry.collection, entry.id, conflict.remote)
                    completed.push(i)
                  } else if (handled === 'merged' && conflict.local !== envelope) {
                    // Merged envelope is stored in conflict.local (the winner)
                    const merged = conflict.local
                    await this.remote.put(this.vault, entry.collection, entry.id, merged)
                    await this.applyRemote(entry.collection, entry.id, merged)
                    completed.push(i)
                    pushed++
                  }
                  // handled === 'deferred': leave in dirty log
                }
              }
            } else {
              throw err
            }
          }
        }
      } catch (err) {
        errors.push(err instanceof Error ? err : new Error(String(err)))
      }
    }

    // Remove completed entries from dirty log (reverse order to preserve indices)
    for (const i of completed.sort((a, b) => b - a)) {
      this.dirty.splice(i, 1)
    }

    this.lastPush = new Date().toISOString()
    try {
      await this.persistMeta()
    } finally {
      // #644 item 1: flush (which clears `_graphBatch` unconditionally, vault.ts:1316-1320) must
      // run even if `persistMeta()` throws — else a throw here leaves the batch open, corrupting
      // the NEXT pull()/push() call's wave with THIS call's stale touches.
      await this.graphBatchController?.flush() // #638 Task 4
    }

    const result: PushResult = { pushed, conflicts, errors, erasures }
    this.emitter.emit('sync:push', result)
    return result
  }

  /** Pull remote records to local adapter. Accepts optional `PullOptions` for partial sync. */
  async pull(options?: PullOptions): Promise<PullResult> {
    await this.ensureLoaded()
    this.graphBatchController?.begin() // #638 Task 4

    let pulled = 0
    const conflicts: Conflict[] = []
    const erasures: ErasureEnforcement[] = []
    const errors: Error[] = []

    // Partial sync: expand the filter to cover satellite pair partners (#591 rule 5b), then the
    // reserved `_dict_*` collections those (expanded) names depend on (#653) — adopters never
    // name `_dict_*` in a `collections` filter, so a named collection's lookup-field dependency
    // must be pulled alongside it or its labels/membership go stale.
    const expanded = options?.collections ? (this.pairExpander?.(options.collections) ?? options.collections) : null
    const filter = expanded ? new Set([...expanded, ...(this.reservedDictExpander?.(expanded) ?? [])]) : null

    try {
      const remoteSnapshot = await this.remote.loadAll(this.vault)

      for (const [collName, records] of Object.entries(remoteSnapshot)) {
        // Partial sync: skip collections not in the filter
        if (filter && !filter.has(collName)) {
          continue
        }

        for (const [id, remoteEnvelope] of Object.entries(records)) {
          // Partial sync: modifiedSince filter — arriving tombstones are exempt (#590):
          // an erasure must never be skipped by partial sync.
          if (
            options?.modifiedSince &&
            remoteEnvelope._ts <= options.modifiedSince &&
            !isTombstoneShape(remoteEnvelope) &&
            !isDeleteMarker(remoteEnvelope)
          ) {
            continue
          }

          try {
            const localEnvelope = await this.local.get(this.vault, collName, id)
            const remoteIsTombstone = isTombstoneShape(remoteEnvelope)

            if (!localEnvelope) {
              // New record from remote (tombstones included — durable erasure evidence)
              await this.applyRemote(collName, id, remoteEnvelope)
              pulled++
            } else if (isTombstoneShape(localEnvelope)) {
              if (remoteIsTombstone) {
                // Both shredded — keep the higher version counter
                if (remoteEnvelope._v > localEnvelope._v) await this.applyRemote(collName, id, remoteEnvelope)
              } else {
                // Terminal rule (#590): a tombstone is never overwritten by a live
                // envelope, regardless of _v. Re-assert the shred outward instead.
                erasures.push(await this.reassertTombstone(collName, id, localEnvelope, remoteEnvelope))
              }
            } else if (remoteIsTombstone) {
              // Terminal rule (#590): an arriving tombstone wins over any local live
              // envelope — even a newer, dirty one. Resolvers are never consulted.
              const wasDirty = this.dirty.some(d => d.collection === collName && d.id === id)
              await this.applyRemote(collName, id, remoteEnvelope)
              this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
              pulled++
              if (wasDirty) erasures.push(this.reportErasure(collName, id, remoteEnvelope, localEnvelope, 'pull'))
            } else if (
              remoteEnvelope._v === localEnvelope._v &&
              isDeleteMarker(remoteEnvelope) !== isDeleteMarker(localEnvelope)
            ) {
              // #589: true concurrent delete-vs-edit at the SAME version. Version order
              // can't break the tie. A per-collection resolver decides if one is set;
              // otherwise DELETE wins (the db-level 'version' default is deliberately NOT
              // consulted — it would resolve a tie to local-wins).
              const resolver = this.conflictResolvers.get(collName)
              if (resolver) {
                const winner = await resolver(id, localEnvelope, remoteEnvelope)
                if (winner !== localEnvelope && winner !== null) {
                  // #589 (review): a novel/merged winner (neither side verbatim) must also be
                  // pushed to remote — mirrors handleConflict's 'merged' handling — so both
                  // sides converge instead of local applying it while remote keeps its old copy.
                  if (winner !== remoteEnvelope) {
                    await this.remote.put(this.vault, collName, id, winner)
                  }
                  await this.applyRemote(collName, id, winner)
                  this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
                  pulled++
                }
                // winner === localEnvelope or null → keep local (its dirty entry, if any, pushes out)
              } else if (isDeleteMarker(remoteEnvelope)) {
                // no resolver → delete wins; the incoming marker is the delete
                await this.applyRemote(collName, id, remoteEnvelope)
                this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
                pulled++
              }
              // no resolver and LOCAL is the marker → keep local marker; its dirty 'put' pushes it outward
            } else if (remoteEnvelope._v > localEnvelope._v) {
              // Remote is newer — check if we have a dirty entry for this
              const isDirty = this.dirty.some(d => d.collection === collName && d.id === id)
              if (isDirty) {
                // Both changed — conflict
                const { handled, conflict } = await this.handleConflict(
                  collName,
                  id,
                  localEnvelope,
                  remoteEnvelope,
                  'pull',
                )
                conflicts.push(conflict)
                if (handled === 'remote') {
                  await this.applyRemote(collName, id, conflict.remote)
                  this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
                  pulled++
                } else if (handled === 'merged' && conflict.local !== localEnvelope) {
                  const merged = conflict.local
                  await this.applyRemote(collName, id, merged)
                  this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
                  pulled++
                }
                // 'local' or 'deferred': push handles it
              } else {
                // Remote is newer, no local changes — update
                await this.applyRemote(collName, id, remoteEnvelope)
                pulled++
              }
            }
            // Same version or local is newer — skip (push will handle)
          } catch (err) {
            errors.push(err instanceof Error ? err : new Error(String(err)))
          }
        }
      }

      // #650 Task 4 (#647) — reserved lookup collections (`_dict_*`/`_lookup_*`) are invisible to
      // `remote.loadAll()` above (the store contract skips `_`-prefixed names) but DO sync via
      // this EXPLICIT, declared registry — not a blanket underscore-glob; other `_` namespaces
      // keep their `loadAll`-skip semantics untouched. Applied through the SAME `applyRemote` path,
      // still inside this try block, BEFORE `persistMeta`/`flush` below — so a pulled vocabulary
      // row lands before the wave (flushed at the end of `pull()`) recomputes any dependent.
      //
      // #647 fix wave 1: `LookupHandle.delete()`/`rename()` now write a version-ordered
      // delete-marker row (mirroring #589) instead of a raw adapter delete, so a removed key is
      // a normal row here too — reachable via `remote.list()` like any other id, applied by
      // version like any other write. The only reserved-tier-specific rule is the same-version
      // tie-break below: reserved lookups have no per-collection conflict-resolver concept
      // (dictionaries are admin-edited, not multi-actor-negotiated), so a converging delete
      // unconditionally wins over a same-version live edit.
      for (const collName of this.reservedLookup?.collections() ?? []) {
        if (filter && !filter.has(collName)) continue
        for (const id of await this.remote.list(this.vault, collName)) {
          try {
            const remoteEnvelope = await this.remote.get(this.vault, collName, id)
            if (!remoteEnvelope) continue

            // Partial sync: modifiedSince filter — a delete marker is exempt, mirroring the
            // main loop's tombstone/marker exemption above: a deletion must never be silently
            // skipped by a time-window partial pull.
            if (
              options?.modifiedSince &&
              remoteEnvelope._ts <= options.modifiedSince &&
              !isDeleteMarker(remoteEnvelope)
            ) {
              continue
            }

            const localEnvelope = await this.local.get(this.vault, collName, id)
            if (!localEnvelope) {
              await this.applyRemote(collName, id, remoteEnvelope)
              pulled++
            } else if (
              remoteEnvelope._v === localEnvelope._v &&
              isDeleteMarker(remoteEnvelope) !== isDeleteMarker(localEnvelope)
            ) {
              // Same-version delete-vs-edit tie (#589's rule, reserved-tier default: no
              // resolver concept here, so delete always wins).
              if (isDeleteMarker(remoteEnvelope)) {
                await this.applyRemote(collName, id, remoteEnvelope)
                this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
                pulled++
              }
              // else: local already holds the marker (a local delete not yet observed
              // remotely) — keep it; push re-asserts it.
            } else if (remoteEnvelope._v > localEnvelope._v) {
              await this.applyRemote(collName, id, remoteEnvelope)
              // Drop any now-superseded local dirty entry so a subsequent push doesn't
              // redundantly re-fight a CAS conflict over content pull just overwrote.
              this.dirty = this.dirty.filter(d => !(d.collection === collName && d.id === id))
              pulled++
            }
          } catch (err) {
            errors.push(err instanceof Error ? err : new Error(String(err)))
          }
        }
      }
    } catch (err) {
      errors.push(err instanceof Error ? err : new Error(String(err)))
    }

    this.lastPull = new Date().toISOString()
    try {
      await this.persistMeta()
    } finally {
      // #644 item 1: flush must run even if `persistMeta()` throws (see push()'s identical guard).
      await this.graphBatchController?.flush() // #638 Task 4
    }

    const result: PullResult = { pulled, conflicts, errors, erasures }
    this.emitter.emit('sync:pull', result)
    return result
  }

  /** Bidirectional sync: pull then push. */
  async sync(options?: { push?: PushOptions; pull?: PullOptions }): Promise<{ pull: PullResult; push: PushResult }> {
    const pullResult = await this.pull(options?.pull)
    const pushResult = await this.push(options?.push)
    return { pull: pullResult, push: pushResult }
  }

  /**
   * Push a specific subset of dirty entries (for sync transactions, ).
   * Entries are matched by collection+id from the dirty log; matched entries
   * are removed from the dirty log on success.
   */
  async pushFiltered(predicate: (entry: DirtyEntry) => boolean): Promise<PushResult> {
    await this.ensureLoaded()

    let pushed = 0
    const conflicts: Conflict[] = []
    const erasures: ErasureEnforcement[] = []
    const errors: Error[] = []
    const completed: number[] = []

    for (let i = 0; i < this.dirty.length; i++) {
      const entry = this.dirty[i]!
      if (!predicate(entry)) continue

      try {
        if (entry.action === 'delete') {
          await this.remote.delete(this.vault, entry.collection, entry.id)
          completed.push(i)
          pushed++
        } else {
          const envelope = await this.local.get(this.vault, entry.collection, entry.id)
          if (!envelope) {
            completed.push(i)
            continue
          }

          if (isTombstoneShape(envelope)) {
            // #590: a tombstone push is an erasure assertion — unconditional,
            // no CAS, no conflict resolution. Erasure always wins.
            await this.remote.put(this.vault, entry.collection, entry.id, envelope)
            completed.push(i)
            pushed++
            continue
          }

          try {
            await this.remote.put(
              this.vault,
              entry.collection,
              entry.id,
              envelope,
              entry.version - 1,
            )
            completed.push(i)
            pushed++
          } catch (err) {
            if (err instanceof ConflictError) {
              const remoteEnvelope = await this.remote.get(this.vault, entry.collection, entry.id)
              if (remoteEnvelope) {
                if (isTombstoneShape(remoteEnvelope)) {
                  // #590: remote already shredded this record — enforce locally,
                  // never resolve. Resolvers must not overrule an erasure.
                  await this.applyRemote(entry.collection, entry.id, remoteEnvelope)
                  erasures.push(this.reportErasure(entry.collection, entry.id, remoteEnvelope, envelope, 'push'))
                  completed.push(i)
                } else if (
                  remoteEnvelope._v === envelope._v &&
                  isDeleteMarker(remoteEnvelope) !== isDeleteMarker(envelope) &&
                  !this.conflictResolvers.get(entry.collection)
                ) {
                  // #589: a same-_v delete-vs-edit tie on the push channel. handleConflict's db-level
                  // 'version' default would resolve it to local-wins; the tie rule consults ONLY the
                  // per-collection resolver, else delete-wins. (When a per-collection resolver IS set,
                  // fall through to handleConflict, which already honors it — incl. the merged case.)
                  if (isDeleteMarker(remoteEnvelope)) {
                    // remote already deleted → converge locally, drop our (edit) push
                    await this.applyRemote(entry.collection, entry.id, remoteEnvelope)
                    completed.push(i)
                  } else {
                    // our local is the marker → force the delete onto the remote (unconditional put)
                    await this.remote.put(this.vault, entry.collection, entry.id, envelope)
                    completed.push(i)
                    pushed++
                  }
                } else {
                  const { handled, conflict } = await this.handleConflict(
                    entry.collection,
                    entry.id,
                    envelope,
                    remoteEnvelope,
                    'push',
                  )
                  conflicts.push(conflict)
                  if (handled === 'local') {
                    await this.remote.put(this.vault, entry.collection, entry.id, conflict.local)
                    completed.push(i)
                    pushed++
                  } else if (handled === 'remote') {
                    await this.applyRemote(entry.collection, entry.id, conflict.remote)
                    completed.push(i)
                  } else if (handled === 'merged' && conflict.local !== envelope) {
                    const merged = conflict.local
                    await this.remote.put(this.vault, entry.collection, entry.id, merged)
                    await this.applyRemote(entry.collection, entry.id, merged)
                    completed.push(i)
                    pushed++
                  }
                }
              }
            } else {
              throw err
            }
          }
        }
      } catch (err) {
        errors.push(err instanceof Error ? err : new Error(String(err)))
      }
    }

    for (const i of completed.sort((a, b) => b - a)) {
      this.dirty.splice(i, 1)
    }

    this.lastPush = new Date().toISOString()
    await this.persistMeta()

    const result: PushResult = { pushed, conflicts, errors, erasures }
    this.emitter.emit('sync:push', result)
    return result
  }

  /** Get current sync status. */
  status(): SyncStatus {
    return {
      dirty: this.dirty.length,
      lastPush: this.lastPush,
      lastPull: this.lastPull,
      online: this.isOnline,
    }
  }

  // ─── Auto-Sync ───────────────────────────────────────────────────

  /** Start auto-sync: listen for online/offline events, optional periodic sync. */
  startAutoSync(intervalMs?: number): void {
    // Online/offline detection
    if (typeof globalThis.addEventListener === 'function') {
      globalThis.addEventListener('online', this.handleOnline)
      globalThis.addEventListener('offline', this.handleOffline)
    }

    // Periodic sync
    if (intervalMs && intervalMs > 0) {
      this.autoSyncInterval = setInterval(() => {
        if (this.isOnline) {
          void this.sync()
        }
      }, intervalMs)
    }
  }

  /** Stop auto-sync and scheduler. */
  stopAutoSync(): void {
    this.stopScheduler()
    if (typeof globalThis.removeEventListener === 'function') {
      globalThis.removeEventListener('online', this.handleOnline)
      globalThis.removeEventListener('offline', this.handleOffline)
    }
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval)
      this.autoSyncInterval = null
    }
  }

  private handleOnline = (): void => {
    this.isOnline = true
    this.emitter.emit('sync:online', undefined as never)
    void this.sync()
  }

  private handleOffline = (): void => {
    this.isOnline = false
    this.emitter.emit('sync:offline', undefined as never)
  }

  /** Apply an envelope to the local store and refresh in-memory views (#598). `action` (#640):
   *  classified HERE — the one choke point that still holds the envelope — so the
   *  cacheInvalidator seam (and, downstream, the dispatch wave) can tell a pulled delete from an
   *  ordinary put. `isTombstoneShape` covers a forgotten-elsewhere record arriving as a shred;
   *  `isDeleteMarker` covers an ordinary (#589) delete — both route to the SAME 'delete' action
   *  (sync delete ≠ forget: freshness only, no shred/residue channel on the receiving side). */
  private async applyRemote(collection: string, id: string, envelope: EncryptedEnvelope): Promise<void> {
    await this.local.put(this.vault, collection, id, envelope)
    const action: 'put' | 'delete' = isTombstoneShape(envelope) || isDeleteMarker(envelope) ? 'delete' : 'put'
    await this.cacheInvalidator?.(collection, id, action)
  }

  /** Record + emit a tombstone enforcement (#590). */
  private reportErasure(
    collection: string, id: string,
    tombstone: EncryptedEnvelope, suppressed: EncryptedEnvelope,
    direction: 'pull' | 'push',
  ): ErasureEnforcement {
    const enforcement: ErasureEnforcement = { vault: this.vault, collection, id, tombstone, suppressed, direction }
    this.emitter.emit('sync:erasure', enforcement)
    return enforcement
  }

  /**
   * Re-assert a local tombstone over a live remote envelope (#590): the shred
   * wins in both directions, regardless of `_v`. Bumps the tombstone to the
   * suppressed envelope's `_v` when higher so per-key version counters stay
   * monotonic on every store; `_by` (the shredding actor) is preserved.
   */
  private async reassertTombstone(
    collection: string, id: string,
    tombstone: EncryptedEnvelope, suppressedRemote: EncryptedEnvelope,
  ): Promise<ErasureEnforcement> {
    let winner = tombstone
    if (suppressedRemote._v > tombstone._v) {
      winner = { ...tombstone, _v: suppressedRemote._v, _ts: new Date().toISOString() }
      await this.applyRemote(collection, id, winner)
    }
    await this.remote.put(this.vault, collection, id, winner)
    // The re-assert already delivered the tombstone remotely — drop any dirty
    // entry so the following push doesn't redundantly re-put it.
    this.dirty = this.dirty.filter(d => !(d.collection === collection && d.id === id))
    return this.reportErasure(collection, id, winner, suppressedRemote, 'pull')
  }

  /**
   * Resolve a conflict, checking per-collection resolvers first,
   * then falling back to the db-level `ConflictStrategy`.
   *
   * Returns the resolved `Conflict` object (possibly with `resolve` set for
   * manual mode) and a `handled` discriminant:
   * - `'local'` — keep the local envelope; push it to remote.
   * - `'remote'` — keep the remote envelope; update local.
   * - `'merged'` — a custom merge fn produced a new envelope stored as `conflict.local`.
   * - `'deferred'` — manual mode, resolve was not called synchronously.
   */
  private async handleConflict(
    collection: string,
    id: string,
    local: EncryptedEnvelope,
    remote: EncryptedEnvelope,
    _phase: 'push' | 'pull',
  ): Promise<{ handled: 'local' | 'remote' | 'merged' | 'deferred'; conflict: Conflict }> {
    const resolver = this.conflictResolvers.get(collection)

    if (resolver) {
      // Per-collection resolver is responsible for emitting sync:conflict
      // (manual policy emits with a resolve callback; LWW/FWW/custom are silent).
      const winner = await resolver(id, local, remote)
      const base: Conflict = {
        vault: this.vault,
        collection,
        id,
        local,
        remote,
        localVersion: local._v,
        remoteVersion: remote._v,
      }
      if (winner === null) return { handled: 'deferred', conflict: base }
      if (winner === local) return { handled: 'local', conflict: base }
      if (winner === remote) return { handled: 'remote', conflict: base }
      // Custom merge fn produced a new envelope — store as conflict.local for the caller
      return {
        handled: 'merged',
        conflict: { ...base, local: winner, localVersion: winner._v },
      }
    }

    // Fall back to db-level strategy — emit once
    const baseConflict: Conflict = {
      vault: this.vault,
      collection,
      id,
      local,
      remote,
      localVersion: local._v,
      remoteVersion: remote._v,
    }
    this.emitter.emit('sync:conflict', baseConflict)
    const side = this.legacyResolve(baseConflict)
    return { handled: side, conflict: baseConflict }
  }

  /** DB-level ConflictStrategy resolution (legacy, kept for backward compat). */
  private legacyResolve(conflict: Conflict): 'local' | 'remote' {
    if (typeof this.strategy === 'function') {
      return this.strategy(conflict)
    }
    switch (this.strategy) {
      case 'local-wins': return 'local'
      case 'remote-wins': return 'remote'
      case 'version':
      default:
        return conflict.localVersion >= conflict.remoteVersion ? 'local' : 'remote'
    }
  }

  // ─── Persistence ─────────────────────────────────────────────────

  private async ensureLoaded(): Promise<void> {
    if (this.loaded) return

    const envelope = await this.local.get(this.vault, '_sync', 'meta')
    if (envelope) {
      const meta = JSON.parse(envelope._data) as SyncMetadata
      this.dirty = [...meta.dirty]
      this.lastPush = meta.last_push
      this.lastPull = meta.last_pull
    }

    this.loaded = true
  }

  private async persistMeta(): Promise<void> {
    const meta: SyncMetadata = {
      _noydb_sync: NOYDB_SYNC_VERSION,
      last_push: this.lastPush,
      last_pull: this.lastPull,
      dirty: this.dirty,
    }

    const envelope: EncryptedEnvelope = {
      _noydb: 1,
      _v: 1,
      _ts: new Date().toISOString(),
      _iv: '',
      _data: JSON.stringify(meta),
    }

    await this.local.put(this.vault, '_sync', 'meta', envelope)
  }
}
